import React, { useEffect, useRef, useState, useCallback } from "react";
import dynamic from "next/dynamic"; // ⬅️ add
import { createPortal } from "react-dom";
import SeoHead from "@/components/SeoHead";
import classes from "./styles.module.scss";
import "@sourceflow-uk/job-search/dist/bundle.css";
import Search from "@/assets/Search.svg";
import JobSearch from "@sourceflow-uk/job-search";
import JobType from "@/../.sourceflow/jobs-contract_type.json";
import Location from "@/../.sourceflow/jobs-Location.json";
import Sectors from "@/../.sourceflow/jobs-Sectors.json";
import Consultants from "@/../.sourceflow/jobs-Consultants.json";
import { FormCheck, Stack } from "react-bootstrap";
import Hero from "@/components/Hero";
import { JobCard } from "@/components";
import { Testimonials } from "@/builder";

const UploadCvForm = dynamic(() => import("@/builder/Form").then((m) => m.default), { ssr: false });
const FORM_ID = "117f2507-7261-4cd8-8e50-d09ed4aa61fe";

function UploadCvModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={classes.modalBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Upload CV form"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={classes.modalPanel} role="document">
        <button className={classes.modalClose} aria-label="Close" onClick={onClose}>
          ×
        </button>
        <div className={classes.modalBody}>
          <UploadCvForm form={FORM_ID} title="Upload your CV" successPageUrl="upload-cv-success" />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function UploadCvButton({ onClick, href = "/upload-cv" }) {
  return (
    <button type="button" onClick={onClick} className={classes.uploadCvBtn} aria-label="Upload CV">
      <span>Upload CV</span>
    </button>
  );
}

export default function JobsPage({ title, canonical }) {
  const [filtersMount, setFiltersMount] = useState(null);
  const [modalOpen, setModalOpen] = useState(false); // ⬅️ add
  const feedRef = useRef(null);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    const root = feedRef.current;
    if (!root) return;

    let mount = null;
    let mounted = false;
    let observer = null;

    const mountButton = () => {
      if (mounted) return;
      const col = root.querySelector(".js-filters-column");
      if (!col) return;

      mount = document.createElement("div");
      mount.className = "sf-uploadcv-mount";
      col.prepend(mount);
      setFiltersMount(mount);
      mounted = true;

      if (observer) observer.disconnect();
    };

    observer = new MutationObserver(mountButton);
    observer.observe(root, { childList: true, subtree: true });
    mountButton();

    return () => {
      if (observer) observer.disconnect();
      if (mount) mount.remove();
    };
  }, []);

  const page = {
    title: { path: "jobs.hero.title", placeholder: "Jobs" },
    background: { path: "jobs.hero.background" },
  };

  return (
    <div>
      <SeoHead title={title} canonical={canonical} />
      <Hero
        background={page.background}
        title={page.title}
        image={"/images/jobPageHero.jpg"}
        className={classes.HeroImage}
      />
      <div className={classes.feed} ref={feedRef}>
        {filtersMount && createPortal(<UploadCvButton onClick={openModal} />, filtersMount)}

        <JobSearch
          options={{
            jobsUrl: "/jobs/",
            urlFilters: {
              mode: "active",
              jobCategories: [Sectors, Location, JobType, Consultants],
              changeURLOnFilterChange: true,
            },
            searchInput: { queryInput: true, locationInput: false },
            searchFiltersOptions: {
              showResetSearchButton: true,
              topLevelCategoryOrder: ["Sectors", "Location", "Contract Type", "Internal Jobs"],
              categorySort: { Sectors: { key: "name", direction: "asc" } },
              exclude: { Consultants: true },
              filterUnselectedElement: <FormCheck />,
              filterSelectedElement: <FormCheck checked />,
            },
            searchResults: ({ results }) => (
              <div className={classes.list}>
                {results && results.length > 0 ? (
                  results.map((job, index) => (
                    <div key={index} className="list-item">
                      <JobCard item={job.job} />
                    </div>
                  ))
                ) : (
                  <div className="alert alert--secondary" role="alert">
                    <h5>Oops! Sorry, we couldn't find any jobs matching your search</h5>
                    <p className="small">Please try removing some filters or searching for something else.</p>
                  </div>
                )}
              </div>
            ),
          }}
          translations={{
            "filters.toggleButtonText": "",
            "searchResults.resultsTotalCount": "Search {{count}} Vacancies",
            "searchResults.buttonText": "View job",
            "input.placeHolder": "Search by keywords",
            "button.text": (
              <Stack className="flex-row align-items-center justify-content-center px-3" gap={2}>
                <Search />
              </Stack>
            ),
          }}
        />
      </div>

      <UploadCvModal open={modalOpen} onClose={closeModal} />
    </div>
  );
}

export async function getStaticProps() {
  return {
    props: {
      title: "All jobs everywhere",
      canonical: "/jobs",
    },
  };
}
